import { Selection, EnterElement, select } from "d3"
import { D3OneToManyRenderable } from "../../../D3/D3OneToManyRenderable"
import { RenderStrategy, SolidColorTraceDataConfig, TraceConfig, TraceDataConfig } from "../../../../Types/Trace"
import { ModalityGraphGroupReactCallbacks } from "../../../../Types/ReactCallbacks"
import React from "react"
import { isGenericAnalytic } from "../../../../Types/AnalysisDetails"
import { EMRDataStreamId, HarmonizedMedication } from "../../../../../../../../Managers/VisualizationManager/ToolBar/Modals/MedicationGraphModalities/Medications"
import { ModalityPageManager } from "../../../../Data/ModalityPageManager"

type D3LegendLabelsConfig = {
	traces: SolidColorTraceDataConfig[]
	graphId: string
	patientModalities: string[]
	patientMedications: HarmonizedMedication[]
	isClickDisabled?: boolean
}

export class D3LegendLabels extends D3OneToManyRenderable<HTMLDivElement, D3LegendLabelsConfig, SolidColorTraceDataConfig, ModalityGraphGroupReactCallbacks> {
	private pageManager: ModalityPageManager

	constructor(root: HTMLDivElement, config: D3LegendLabelsConfig, pageManager: ModalityPageManager, reactCallbacks: ModalityGraphGroupReactCallbacks) {
		super(root, config, "d3-legend-labels", reactCallbacks)
		this.pageManager = pageManager
		this.mount()
	}

	protected datumIdentifier(datum: SolidColorTraceDataConfig): string | number {
		return `${datum.name}-${datum.color}`
	}

	protected getConfigs(): SolidColorTraceDataConfig[] {
		return this.config.traces.filter(trace => trace.name !== undefined)
	}

	protected enter(newElements: Selection<EnterElement, SolidColorTraceDataConfig, Element, any>): Selection<HTMLDivElement, SolidColorTraceDataConfig, Element, any> {
		const elements = newElements
			.append("div")
			.attr("class", this.className)
			.style("white-space", "nowrap")
			.style("cursor", trace => this.getCursor(trace))
			.attr("title", trace => this.traceFoundInRecording(trace as TraceConfig) ? "" : `${trace.name} data is not available.`)
			.style("color", trace => trace.color)
			.style("-webkit-text-fill-color", trace => trace.color ) // for screenshots
			.style("opacity", trace => (trace.isVisible ?? true) && this.traceFoundInRecording(trace as TraceConfig) ? 1 : 0.2)
			.text(trace => trace.name)

		if (!this.config.isClickDisabled) {
			elements.each((config, index, nodes) => this.attachListeners(config, index, nodes))
		}

		return elements
	}

	protected update(updatedElements: Selection<HTMLDivElement, SolidColorTraceDataConfig, Element, any>): Selection<HTMLDivElement, SolidColorTraceDataConfig, Element, any> {
		return updatedElements
			.style("cursor", trace => this.getCursor(trace))
			.attr("title", trace => this.traceFoundInRecording(trace as TraceConfig) ? "" : `${trace.name} data is not available.`)
			.style("color", trace => trace.color)
			.style("-webkit-text-fill-color", trace => trace.color ) // for screenshots
			.style("opacity", trace => (trace.isVisible ?? true) && this.traceFoundInRecording(trace as TraceConfig) ? 1 : 0.2)
			.text(trace => trace.name)
	}

	private traceFoundInRecording = (trace: TraceConfig) => {
		if (trace.renderStrategy === RenderStrategy.DELTA) {
			return this.dataConfigFound(trace.first) && this.dataConfigFound(trace.second)
		}

		return this.dataConfigFound(trace)
	}

	private getCursor(trace: TraceDataConfig): string {
		if (this.config.isClickDisabled) {
			return "default"
		}

		if (this.traceFoundInRecording(trace as TraceConfig)) {
			return "pointer"
		}

		return "not-allowed"
	}

	private dataConfigFound = (trace: TraceDataConfig) => {
		if (trace.isEMRData) {
			const emrDataId: EMRDataStreamId = JSON.parse(trace.id)

			return !!this.pageManager.getEMRData().find(harmonized => (
				harmonized.id === emrDataId.id && 
				!!harmonized.dataStreams.find(stream => stream.code === emrDataId.code)
			))
		}

		return (
			this.config.patientModalities.includes(trace.rawDataKey) ||
			(trace.onDemandAnalysis?.analytic !== undefined && !isGenericAnalytic(trace.onDemandAnalysis?.analytic))
		)

	}

	private attachListeners = (config: SolidColorTraceDataConfig, index: number, nodes: ArrayLike<HTMLDivElement>) => {
		const selection = select(nodes[index]) as Selection<any, SolidColorTraceDataConfig, any, any>

		if (!this.traceFoundInRecording(config as TraceConfig)) {
			return
		}

		selection
			.on("mousedown", (event: React.MouseEvent, config) => {
				if (event.buttons === 1) {
					this.reactCallbacks.toggleTraceVisibility(this.config.graphId, config.id, config.isEMRData)
				}
			})
			.on("contextmenu", (event: React.MouseEvent, config) => {
				event.preventDefault()
				this.reactCallbacks.openTraceVisibilityContextMenu(event, config, this.config.graphId)
			})
	}
}
